/**
 * Pure freshness-tier classification for the Health_Cache.
 *
 * A customer's cache freshness is driven by its Last_Fetch_Time. The age of
 * the cache (`Cache_Age = now - lastFetchTime`) maps to one of three tiers:
 *
 * - `fresh`:   `age < 7 min`           — serve cache, no refetch, no full loader
 * - `stale`:   `7 min <= age <= 1 hr`  — serve cache, background revalidate
 * - `expired`: `age > 1 hr`            — block with the full loading page
 *
 * Boundary handling (from the glossary): the 7-minute boundary belongs to
 * Stale, and the 1-hour boundary belongs to Stale (Expired is strictly greater
 * than 1 hour).
 *
 * Satisfies:
 * - Requirement 2.1: Fresh_Tier when Cache_Age < 7 minutes.
 * - Requirement 3.3: Stale_Tier when 7 minutes <= Cache_Age <= 1 hour.
 * - Requirement 4.1: Expired_Tier when Cache_Age > 1 hour.
 */

export type FreshnessTier = 'fresh' | 'stale' | 'expired';

/** Seven minutes in milliseconds — the fresh/stale boundary. */
export const SEVEN_MINUTES_MS = 7 * 60 * 1000;

/** One hour in milliseconds — the stale/expired boundary. */
export const ONE_HOUR_MS = 60 * 60 * 1000;

/**
 * Classify the freshness tier for a cache entry given its Last_Fetch_Time and
 * the current time.
 *
 * @param lastFetchTime epoch-ms timestamp of the last page-1 fetch.
 * @param now epoch-ms timestamp of the moment the decision is made.
 * @returns the freshness tier: `'fresh' | 'stale' | 'expired'`.
 */
export function computeTier(lastFetchTime: number, now: number): FreshnessTier {
  const age = now - lastFetchTime;

  if (age < SEVEN_MINUTES_MS) {
    // age < 7 min -> fresh (Req 2.1).
    return 'fresh';
  }

  if (age <= ONE_HOUR_MS) {
    // 7 min <= age <= 1 hr -> stale; both boundaries are inclusive (Req 3.3).
    return 'stale';
  }

  // age > 1 hr -> expired (Req 4.1).
  return 'expired';
}
